/**
 * Consumer for the native App Lock bridge (`window.PlaintrAppLockBridge`).
 *
 * The bridge is injected only inside the Android wrapper's WebView on the
 * confirmed remote origin. In a normal browser, or before the shim has
 * installed, every function here is a safe no-op.
 *
 * The native shim installs on `onPageFinished`, which fires after this
 * document's own scripts have already started running — so React can
 * resolve auth state before the bridge exists. To avoid losing that first
 * resolved value, this module remembers the last resolved boolean and
 * resends it either when the shim's `"plaintr:applockbridgeready"` event
 * fires, or immediately if the bridge is already present when this module's
 * listener attaches — covering both possible orderings.
 *
 * The only value ever sent is a strict boolean; a non-boolean argument is
 * silently rejected.
 */

export const APP_LOCK_BRIDGE_READY_EVENT = "plaintr:applockbridgeready";

/** `null` means "never resolved yet" (still loading/hydrating). */
let lastResolvedAuthenticated = null;
let readyListenerInstalled = false;

function getAppLockBridge() {
  if (typeof window === "undefined") {
    return null;
  }
  const bridge = window.PlaintrAppLockBridge;
  if (!bridge || typeof bridge.notifyAuthenticatedUserChanged !== "function") {
    return null;
  }
  return bridge;
}

function sendToBridge(authenticated) {
  const bridge = getAppLockBridge();
  if (!bridge) {
    return;
  }
  try {
    bridge.notifyAuthenticatedUserChanged(authenticated);
  } catch (error) {
    console.error(
      "PlaintrAppLockBridge.notifyAuthenticatedUserChanged threw unexpectedly:",
      error?.message || error,
    );
  }
}

function handleBridgeReady() {
  if (lastResolvedAuthenticated === null) {
    return;
  }
  sendToBridge(lastResolvedAuthenticated);
}

function installReadyListener() {
  if (readyListenerInstalled || typeof window === "undefined") {
    return;
  }
  window.addEventListener(APP_LOCK_BRIDGE_READY_EVENT, handleBridgeReady);
  readyListenerInstalled = true;
  if (getAppLockBridge()) {
    handleBridgeReady();
  }
}

// Installed at module import time, well before auth is typically resolved.
installReadyListener();

/**
 * Report a resolved authentication state to the native bridge, if present.
 * A no-op in a normal browser. Rejects any non-boolean argument.
 *
 * @param {boolean} authenticated
 */
export function notifyAuthenticatedUserChanged(authenticated) {
  if (typeof authenticated !== "boolean") {
    return;
  }
  lastResolvedAuthenticated = authenticated;
  sendToBridge(authenticated);
}

/** Test-only: resets this module's singleton state. Never called from production code. */
export function __resetForTests() {
  if (readyListenerInstalled && typeof window !== "undefined") {
    window.removeEventListener(APP_LOCK_BRIDGE_READY_EVENT, handleBridgeReady);
  }
  lastResolvedAuthenticated = null;
  readyListenerInstalled = false;
  installReadyListener();
}
